import { useUserStore } from '../../../store/useUserStore';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import moment from 'moment';
import Url from '../../../data/url';
import {
  useQnaDetailQuery,
  useExpertAnswerQuery,
  useQnaCommentsQuery,
  useCreateCommentMutation,
  useToggleLikeMutation,
} from '../../../queries';
import { COMMUNITY_USER_TYPE, COMPLAINT_TARGET_TYPE } from '../../../data/define';
import useInfiniteScroll from '../../../hook/useInfiniteScroll';
import IcLike from '../../../assets/image/community/ic_like.svg';
import IcLikeFilled from '../../../assets/image/community/ic_like_filled.svg';
import IcProfile from '../../../assets/image/ic_profile.svg';
import IcChevronRight from '../../../assets/image/ic_chevron_right.svg';
import IcPrivate from '../../../assets/image/community/ic_private.svg';
import FullHeightContainer from '../../common/FullHeightContainer';
import TopHeaderWithPrevOption from '../../common/TopHeaderWithPrevOption';
import Divider from '../../common/Divider';
import CommentCard from '../CommentCard';
import ExpertAnswer from './ExpertAnswer';
import BottomFixedTextField from '../../common/BottomFixedTextField';
import OptionDialog from './OptionDialog';
import ComplaintDialog from '../ComplaintDialog';
import { useTranslation } from '../../../hook/useTranslation';

const DateSort = {
  Registration: 1, // 'ASC'
  Latest: 2, // 'DESC'
};

const LIMIT = 10;

interface PostDetailProps {
  drawerOpen: boolean;
  onClickPrev: () => void;
  postId?: number | null;
  reload?: () => void;
}

function PostDetail({
  drawerOpen,
  onClickPrev,
  postId = null,
  reload = () => {},
}: PostDetailProps) {
  const navigate = useNavigate();
  const user = useUserStore();
  const { t } = useTranslation();
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [optionOpenId, setOptionOpenId] = useState<number | null>(null);
  const [complaintOpenId, setComplaintOpenId] = useState<number | null>(null);
  const [dateSorting, setDateSorting] = useState(DateSort.Latest);
  const [inputValue, setInputValue] = useState('');
  const [comments, setComments] = useState<any[]>([]);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const commentsSectionRef = useRef<HTMLDivElement>(null);

  // 1. 질문상세 조회 Query
  const { data: detail } = useQnaDetailQuery(postId || 0, user.id || 0, {
    enabled: drawerOpen && !!user?.id && !!postId,
  });

  // 2. 전문가 답변 조회 Query
  const { data: expertAnswerList } = useExpertAnswerQuery(
    { questionId: postId || 0 },
    { enabled: !!postId && !!detail && detail.isExpertAnswer }
  );

  const expertAnswer = expertAnswerList?.[0] || null;

  // 3. 질문 댓글 조회 Query
  const commentsParams = useMemo(
    () => ({
      questionId: postId || 0,
      limit: LIMIT,
      page,
      order: dateSorting === DateSort.Registration ? 'ASC' : 'DESC',
      sortBy: dateSorting === DateSort.Registration ? 'createdAt' : 'updatedAt',
    }),
    [postId, page, dateSorting]
  );

  const {
    data: commentsData,
    isLoading,
    refetch: refetchComments,
  } = useQnaCommentsQuery(commentsParams, { enabled: drawerOpen && !!postId });

  useEffect(() => {
    if (commentsData) {
      if (page === 1) {
        setComments(commentsData.items);
      } else {
        setComments((prev) => [...prev, ...commentsData.items]);
      }
      setTotalCount(commentsData.total);
    }
  }, [commentsData, page]);

  const refreshComments = useCallback(() => {
    setPage(1);
    setComments([]);
    setHasMore(true);
    refetchComments();
  }, [refetchComments]);

  // 4. 댓글 작성 Mutation
  const { mutate: createComment } = useCreateCommentMutation(postId || 0);

  // 5. 좋아요 토글 Mutation
  const { mutate: toggleLike } = useToggleLikeMutation(postId || 0, user.id || 0);

  // infinite scroll
  const { lastItemRef } = useInfiniteScroll({
    hasMore,
    isLoading,
    onLoadMore: () => {
      if (totalCount <= page * LIMIT) {
        setHasMore(false);
        return;
      }
      setPage((prev) => prev + 1);
    },
  });

  const handleCommentSubmit = useCallback(async () => {
    if (!inputValue.trim()) return;
    createComment(
      { content: inputValue },
      {
        onSuccess: () => {
          setInputValue('');
          refreshComments();
        },
        onError: (err) => {
          console.error('Failed to create comment', err);
          toast.error(t('community.toast_comment_create_fail'));
        },
      }
    );
  }, [inputValue, createComment, refreshComments, t]);

  const handleLikeToggle = useCallback(() => {
    if (user?.isLoggedIn) {
      toggleLike(null, {
        onError: (err) => {
          console.error('Failed to toggle like', err);
          toast.error(t('community.qna.toast_like_fail'));
        },
      });
    } else {
      navigate(Url.Login);
    }
  }, [user, navigate, toggleLike, t]);

  const handleComplaintReport = (selectedId: number) => {
    setComplaintOpenId(selectedId);
    setOptionOpenId(null);
  };

  // 옵션버튼 클릭
  const handleOptionClick = useCallback((postId: number) => {
    setOptionOpenId(postId);
  }, []);

  // 상단으로 스크롤
  const scrollToTop = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // 댓글 리스트 스크롤 감지
  useEffect(() => {
    const handleScroll = () => {
      if (!commentsSectionRef.current || !scrollContainerRef.current) return;

      const container = scrollContainerRef.current;
      const commentsSection = commentsSectionRef.current;

      // 댓글 섹션의 상대 위치 계산
      const containerRect = container.getBoundingClientRect();
      const commentsRect = commentsSection.getBoundingClientRect();

      // 댓글 섹션이 화면 상단에 닿았는지 확인
      const isCommentsAtTop = commentsRect.top <= containerRect.top;

      setShowScrollToTop(isCommentsAtTop && comments?.length > 0);
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      // 초기 체크
      handleScroll();

      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [comments]);

  return (
    <FullHeightContainer>
      <TopHeaderWithPrevOption
        title={
          detail?.userType === COMMUNITY_USER_TYPE.EXPERT
            ? t('community.qna.expert_answer')
            : t('my.posts.user_answer')
        }
        isMine={detail?.author?.id === user?.id}
        selectedId={postId}
        onClickPrev={onClickPrev}
        reloadReport={reload}
      />
      <div className="pageInner" ref={scrollContainerRef}>
        <div className="w-full flex justify-start gap-2 items-center border-b border-[#EAEAEA] py-3 my-6">
          <div className="shrink-0 min-w-[52px] max-w-[52px] min-h-[52px] max-h-[52px] bg-[#FFF] border border-[#EAEAEA] overflow-hidden flex items-center justify-center rounded-[24px]">
            <img
              src={detail?.author?.imageUrl ? detail?.author?.imageUrl : IcProfile}
              alt="profile"
              className="w-[52px] object-cover rounded-full"
            />
          </div>
          <div className="gap-1 flex flex-col items-start justify-between font-[400] text-[#363636] text-[12px]">
            <div className="font-[700] text-[#1C1C1C] text-[16px]">{detail?.author?.nickName}</div>
            <div className="font-[400] text-[#969696] text-[12px]">
              {moment(detail?.createdAt).format('YY.MM.DD')}
            </div>
          </div>
        </div>
        <div className="w-full text-left p-5 bg-[#F8F8F8] rounded-[8px] border border-[#E0E0E0] text-[14px] text-[#363636] font-[400]">
          <div className="flex items-center gap-1 justify-start font-[700] text-[#1C1C1C] text-[16px] mb-2">
            <span>{t('community.qna.question')}</span>
            {detail?.isPrivate && (
              <img src={IcPrivate} alt="private" className="w-[15px] h-[15px]" />
            )}
          </div>
          <div className="whitespace-pre-line">{detail?.content}</div>
        </div>
        {expertAnswer && <ExpertAnswer data={expertAnswer} />}
        <div className="w-full mt-5">
          <button
            type="button"
            className="px-4 py-2 rounded-[25px] flex justify-center items-center gap-1 border border-[#E0E0E0]"
            onClick={handleLikeToggle}
            style={detail?.isLiked ? { backgroundColor: '#F0FCFC', borderColor: '#5EBEBB' } : {}}
          >
            <img src={detail?.isLiked ? IcLikeFilled : IcLike} alt="like" />
            <span className="text-[14px] text-[#363636]">
              {detail?.likeCount === 0 ? t('community.like') : `${detail?.likeCount}`}
            </span>
          </button>
        </div>
        <div className="my-[24px] w-full">
          <Divider />
        </div>
        <div ref={commentsSectionRef} className="w-full flex justify-between mb-4">
          <div className="flex justify-start items-center text-[16px] font-[700] gap-1">
            {t('community.comment_count', { count: totalCount || 0 })}
          </div>
          <div className="flex justify-end items-center text-[14px] font-[700] gap-2">
            <button
              type="button"
              style={{
                color:
                  dateSorting === DateSort.Registration && comments?.length !== 0
                    ? '#363636'
                    : '#969696',
              }}
              onClick={() => setDateSorting(DateSort.Registration)}
              disabled={comments?.length === 0}
            >
              {t('common.sort_registration')}
            </button>
            <button
              type="button"
              style={{
                color:
                  dateSorting === DateSort.Latest && comments?.length !== 0 ? '#363636' : '#969696',
              }}
              onClick={() => setDateSorting(DateSort.Latest)}
              disabled={comments?.length === 0}
            >
              {t('common.sort_latest')}
            </button>
          </div>
        </div>
        <div className="w-full">
          {comments?.length >= 1 ? (
            comments?.map((comment, idx) => (
              <CommentCard
                key={comment.id}
                comment={comment}
                optionClick={handleOptionClick}
                ref={idx === comments.length - 1 ? lastItemRef : undefined}
              />
            ))
          ) : (
            <div className="text-center text-[#767676] text-[14px] mt-20">
              {t('community.no_comments').split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  {i === 0 && <br />}
                </span>
              ))}
            </div>
          )}
          <OptionDialog
            open={optionOpenId !== null}
            onClose={() => setOptionOpenId(null)}
            isUsersPost={
              comments?.find((item) => item.id === optionOpenId)?.author?.id === user?.id
            }
            selectedId={optionOpenId as number}
            isPostNotComment={false}
            reload={refreshComments}
            onComplaintClick={handleComplaintReport}
          />
          {complaintOpenId !== null && (
            <ComplaintDialog
              open={complaintOpenId !== null}
              onClose={() => setComplaintOpenId(null)}
              targetType={COMPLAINT_TARGET_TYPE.QUESTION_ANSWER}
            />
          )}
        </div>
      </div>
      <BottomFixedTextField
        inputValue={inputValue}
        setInputValue={setInputValue}
        onSubmit={handleCommentSubmit}
      />
      <button
        type="button"
        onClick={scrollToTop}
        className="fixed bg-[#f3f3f3] rounded-[24px] w-[48px] h-[48px] flex items-center justify-center shadow-[0px_0px_10px_0px_rgba(29,32,32,0.07)] hover:opacity-80 transition-all duration-300 z-50"
        style={{
          bottom: '80px',
          right: '20px',
          opacity: showScrollToTop ? 1 : 0,
          visibility: showScrollToTop ? 'visible' : 'hidden',
          pointerEvents: showScrollToTop ? 'auto' : 'none',
        }}
      >
        <img
          src={IcChevronRight}
          alt="상단으로"
          className="w-[26px] h-[26px] rotate-[-90deg]"
          style={{
            filter:
              'brightness(0) saturate(100%) invert(21%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(95%)',
          }}
        />
      </button>
    </FullHeightContainer>
  );
}

export default PostDetail;
